import type { AudioService } from "./ports/AudioService";
import { AudioSettingsStore } from "./AudioSettingsStore";

/**
 * Bridges `AudioSettingsStore` (UI-driven prefs) to `AudioService` (runtime
 * audio engine). Pushes a fresh snapshot to the engine on every store
 * change.
 *
 * Two settings flow through here:
 *   - `masterVolume` (0–1) → drives `audioService.control.setMasterVolume`.
 *     The master gain stage sits in front of every bus; setting it to 0
 *     silences everything, including music + ambient, without disabling
 *     the individual bus enables.
 *   - `musicEnabled` (boolean) → drives `setBusEnabled("music", ...)`.
 *     Independent of master volume — the player's "music off" preference
 *     persists across mute/unmute cycles.
 *
 * The sfx / voice / ui / ambient buses are NOT toggled here. They default
 * to enabled in `AudioMixer` and stay that way; master volume mutes them
 * when set to 0.
 */
export class AudioSettingsService {
  private unsubscribe?: () => void;

  constructor(
    private readonly store: AudioSettingsStore,
    private readonly audioService: AudioService,
  ) {}

  public start(): void {
    this.applyFromSnapshot();

    this.unsubscribe = this.store.subscribe(() => {
      this.applyFromSnapshot();
    });
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  /** Drag handler for the menu's sound slider. Direct write, no memory update. */
  public setMasterVolume(volume: number): void {
    this.store.setMasterVolume(volume);
  }

  /** Click handler for the menu's sound icon. Toggles mute and restores prior volume. */
  public toggleMute(): void {
    this.store.toggleMute();
  }

  public toggleMusic(): void {
    const snap = this.store.getSnapshot();

    if (snap.masterVolume === 0) {
      return;
    }

    this.store.setMusicEnabled(!snap.musicEnabled);
  }

  public setMusicEnabled(enabled: boolean): void {
    const snap = this.store.getSnapshot();

    if (snap.masterVolume === 0 && enabled) {
      return;
    }

    this.store.setMusicEnabled(enabled);
  }

  private applyFromSnapshot(): void {
    const snap = this.store.getSnapshot();

    this.audioService.control.setMasterVolume(snap.masterVolume);
    this.audioService.control.setBusEnabled("music", snap.musicEnabled);
  }
}
